import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';

function levelForPoints(points) {
  if (points >= 500) return { label: 'Expert', labels: { zh: '大神' }, color: '#f59e0b' };
  if (points >= 100) return { label: 'Pro', labels: { zh: '达人' }, color: '#108a00' };
  if (points >= 20) return { label: 'Active', labels: { zh: '活跃' }, color: '#108a00' };
  return { label: 'New', labels: { zh: '新人' }, color: '#6b7280' };
}

function normalizePoints(points) {
  const value = Number(points);
  return Number.isFinite(value) ? value : 0;
}

function normalizeGrowthLevel(user) {
  const fallback = levelForPoints(normalizePoints(user.points));
  const level = user.growth_level;
  if (!level || typeof level !== 'object') return fallback;
  if (level.labels?.zh || level.label) return { ...fallback, ...level };
  return fallback;
}

export default function Points() {
  const { language, copy } = useAdminLanguage();
  const pointsCopy = copy.points;
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');

  // 手动调整对话框状态
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const [modalChange, setModalChange] = useState('');
  const [modalReason, setModalReason] = useState('');
  const [modalError, setModalError] = useState('');

  const fetchPoints = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/points', { params: { keyword } })
      .then(res => {
        setUsers(res.data.users || []);
        setLogs(res.data.logs || []);
      })
      .catch(err => setError(err.response?.data?.error || pointsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [keyword, pointsCopy.errors.loadFailed]);

  useEffect(() => { fetchPoints(); }, [fetchPoints]);

  const filteredUsers = users.filter(u =>
    !keyword.trim() || (u.username || '').toLowerCase().includes(keyword.toLowerCase()) || (u.email || '').toLowerCase().includes(keyword.toLowerCase())
  ).sort((a, b) => normalizePoints(b.points) - normalizePoints(a.points));

  const openAdjust = (user) => {
    setModalUser(user);
    setModalChange('');
    setModalReason('');
    setModalError('');
    setModalOpen(true);
  };
  const submitAdjust = async () => {
    const change = Number(modalChange);
    setModalError('');
    if (!change || !modalReason.trim()) {
      setModalError(pointsCopy.errors.requiredFields);
      return;
    }
    try {
      await api.post(`/admin/points/${modalUser.id}/adjust`, { change, reason: modalReason.trim() });
      setModalOpen(false);
      fetchPoints();
    } catch (err) {
      setModalError(err.response?.data?.error || pointsCopy.errors.adjustFailed);
    }
  };

  const totalPoints = users.reduce((s, u) => s + normalizePoints(u.points), 0);

  return (
    <div>
      <div className="page-title">{pointsCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="card stat-card"><div className="stat-label">{pointsCopy.stats.users}</div><div className="stat-value">{users.length}</div></div>
        <div className="card stat-card"><div className="stat-label">{pointsCopy.stats.totalPoints}</div><div className="stat-value" style={{ color: '#1dbf73' }}>{totalPoints.toLocaleString()}</div></div>
        <div className="card stat-card"><div className="stat-label">{pointsCopy.stats.logs}</div><div className="stat-value" style={{ color: '#3b82f6' }}>{logs.length}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16 }}>
        {/* 用户积分列表 */}
        <div className="card">
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div className="search-input" style={{ flex: 1 }}>
              <input placeholder={pointsCopy.searchPlaceholder} value={keyword} onChange={e => setKeyword(e.target.value)} />
            </div>
            <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>{pointsCopy.userCount(filteredUsers.length)}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{pointsCopy.columns.rank}</th><th>{pointsCopy.columns.user}</th><th>{pointsCopy.columns.email}</th><th>{pointsCopy.columns.points}</th><th>{pointsCopy.columns.level}</th><th>{pointsCopy.columns.actions}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{pointsCopy.loading}</td></tr>
                ) : filteredUsers.map((u, i) => (
                  (() => {
                    const level = normalizeGrowthLevel(u);
                    return (
                      <tr key={u.id}>
                        <td style={{ fontWeight: 700, color: i < 3 ? '#ffa500' : '#999' }}>{i < 3 ? ['🥇','🥈','🥉'][i] : `#${i + 1}`}</td>
                        <td style={{ fontWeight: 600 }}>{u.username}</td>
                        <td style={{ color: '#6b7280' }}>{u.email}</td>
                        <td style={{ color: '#1dbf73', fontWeight: 700 }}>{u.points}</td>
                        <td>
                          <span className="points-level-badge" style={{ color: level.color || '#6b7280', fontWeight: 700 }}>
                            {level.labels?.[language] || level.label}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline" onClick={() => openAdjust(u)}>{pointsCopy.actions.adjust}</button>
                        </td>
                      </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 积分日志 */}
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>📜 {pointsCopy.recentLogs}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
            {logs.map(l => (
              <div key={l.id} style={{ padding: 10, border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>{l.user}</strong>
                  <span style={{ color: l.change > 0 ? '#1dbf73' : '#ef4444', fontWeight: 700 }}>
                    {l.change > 0 ? '+' : ''}{l.change}
                  </span>
                </div>
                <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>{l.reason}</div>
                <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>{l.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 调整积分对话框 */}
      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }} onClick={() => setModalOpen(false)}>
          <div className="card" style={{ width: 420, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: 0, marginBottom: 14 }}>{pointsCopy.adjustModal.title(modalUser.username)}</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{pointsCopy.adjustModal.changeLabel}</label>
              <input type="number" placeholder={pointsCopy.adjustModal.changePlaceholder} value={modalChange} onChange={e => { setModalChange(e.target.value); setModalError(''); }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{pointsCopy.adjustModal.reasonLabel}</label>
              <input type="text" placeholder={pointsCopy.adjustModal.reasonPlaceholder} value={modalReason} onChange={e => { setModalReason(e.target.value); setModalError(''); }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            {modalError && <div className="card" style={{ color: '#b91c1c', background: '#fef2f2', marginBottom: 14 }}>{modalError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setModalOpen(false)}>{pointsCopy.actions.cancel}</button>
              <button className="btn btn-primary" onClick={submitAdjust}>{pointsCopy.actions.confirm}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
